import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useVirtualizer } from "@tanstack/react-virtual";
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef } from "react";
import { LOADING_TRANSCRIPT_SEGMENT_HEIGHT } from "~/components/AssetModal/shared/components/TranscriptPanel/components/TranscriptSegmentList/components/TranscriptSegment/components/EmptyTranscriptSegment/LoadingTranscriptSegments";
import { TranscriptSegmentMeasure } from "~/components/AssetModal/shared/components/TranscriptPanel/components/TranscriptSegmentList/components/TranscriptSegment/TranscriptSegmentMeasure";
import { usePersistItemPositionOnLoad } from "~/components/AssetModal/shared/components/TranscriptPanel/components/TranscriptSegmentList/hooks/usePersistItemPositionOnLoad";
import { TranscriptVideoInfo } from "~/components/AssetModal/shared/components/TranscriptPanel/components/TranscriptVideoInfo";
const getSegmentCacheKey = (segment)=>"".concat(segment.type, "-").concat(segment.startTimeMs, "-").concat(segment.endTimeMs);
const DEFAULT_WORD_HEIGHT = 24;
const DEFAULT_SEGMENT_HEIGHT = 82;
const DEFAULT_HEADER_HEIGHT = 100;
const _TranscriptSegmentsVirtualizedList = /*#__PURE__*/ forwardRef((param, forwardedRef)=>{
    let { segments, children, currentLineObserver } = param;
    var _listRef_current, _segmentsHeightCache_current, _virtualItems_;
    const listRef = useRef(null);
    const scrollElementRef = useRef(null);
    const measureHandle = useRef(null);
    const segmentsHeightCache = useRef();
    useEffect(()=>{
        segmentsHeightCache.current = new Map();
    }, []);
    const rowsCount = segments.length;
    const estimateSize = useCallback((index)=>{
        var _segmentsHeightCache_current;
        let size = null;
        const segment = segments[index];
        const segmentCacheKey = getSegmentCacheKey(segment);
        const currentCachedValue = (_segmentsHeightCache_current = segmentsHeightCache.current) === null || _segmentsHeightCache_current === void 0 ? void 0 : _segmentsHeightCache_current.get(segmentCacheKey);
        if (!!currentCachedValue) {
            return currentCachedValue.totalHeight;
        } else {
            var _segmentsHeightCache_current1;
            if (segment.type === "empty") {
                size = {
                    totalHeight: LOADING_TRANSCRIPT_SEGMENT_HEIGHT,
                    linesPositions: [
                        {
                            startTimeMs: segment.startTimeMs,
                            endTimeMs: segment.endTimeMs,
                            offset: 0
                        }
                    ],
                    lineHeight: DEFAULT_WORD_HEIGHT,
                    segmentHeaderHeight: 0
                };
            } else {
                var _measureHandle_current;
                var _measureHandle_current_getSegmentHeight;
                size = (_measureHandle_current_getSegmentHeight = (_measureHandle_current = measureHandle.current) === null || _measureHandle_current === void 0 ? void 0 : _measureHandle_current.getSegmentHeight(segment)) !== null && _measureHandle_current_getSegmentHeight !== void 0 ? _measureHandle_current_getSegmentHeight : {
                    totalHeight: DEFAULT_SEGMENT_HEIGHT,
                    linesPositions: [
                        {
                            startTimeMs: segment.startTimeMs,
                            endTimeMs: segment.endTimeMs,
                            offset: 0
                        }
                    ],
                    lineHeight: DEFAULT_WORD_HEIGHT,
                    segmentHeaderHeight: 0
                };
            }
            (_segmentsHeightCache_current1 = segmentsHeightCache.current) === null || _segmentsHeightCache_current1 === void 0 ? void 0 : _segmentsHeightCache_current1.set(segmentCacheKey, size);
        }
        return size.totalHeight;
    }, [
        segments
    ]);
    var _listRef_current_offsetTop;
    const virtualizer = useVirtualizer({
        count: rowsCount,
        getScrollElement: ()=>scrollElementRef.current,
        paddingEnd: 30,
        estimateSize,
        overscan: 2,
        getItemKey: (index)=>getSegmentCacheKey(segments[index]),
        scrollMargin: (_listRef_current_offsetTop = (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.offsetTop) !== null && _listRef_current_offsetTop !== void 0 ? _listRef_current_offsetTop : DEFAULT_HEADER_HEIGHT
    });
    const { createPositionsSnapshot } = usePersistItemPositionOnLoad({
        virtualizer,
        segments
    });
    useImperativeHandle(forwardedRef, ()=>({
            getScrollElement: ()=>virtualizer.scrollElement,
            createPositionsSnapshot,
            getSegmentHeightCache: (segmentIndex)=>{
                var _segmentsHeightCache_current;
                const segmentKey = getSegmentCacheKey(segments[segmentIndex]);
                var _segmentsHeightCache_current_get;
                return (_segmentsHeightCache_current_get = (_segmentsHeightCache_current = segmentsHeightCache.current) === null || _segmentsHeightCache_current === void 0 ? void 0 : _segmentsHeightCache_current.get(segmentKey)) !== null && _segmentsHeightCache_current_get !== void 0 ? _segmentsHeightCache_current_get : null;
            },
            getSegmentTopPosition: (segmentIndex)=>{
                var _virtualizer_measurementsCache_segmentIndex;
                var _virtualizer_measurementsCache_segmentIndex_start;
                return (_virtualizer_measurementsCache_segmentIndex_start = (_virtualizer_measurementsCache_segmentIndex = virtualizer.measurementsCache[segmentIndex]) === null || _virtualizer_measurementsCache_segmentIndex === void 0 ? void 0 : _virtualizer_measurementsCache_segmentIndex.start) !== null && _virtualizer_measurementsCache_segmentIndex_start !== void 0 ? _virtualizer_measurementsCache_segmentIndex_start : null;
            },
            getScrollMargin: ()=>virtualizer.options.scrollMargin
        }));
    const virtualItems = virtualizer.getVirtualItems();
    const onWordResize = useCallback(()=>{
        var _segmentsHeightCache_current;
        (_segmentsHeightCache_current = segmentsHeightCache.current) === null || _segmentsHeightCache_current === void 0 ? void 0 : _segmentsHeightCache_current.clear();
        virtualizer.measure();
    }, [
        virtualizer
    ]);
    var _virtualItems__start;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "pointer-events-none absolute top-0 w-full pl-4 opacity-0 scrollbar scrollbar-track-grey-2 scrollbar-thumb-grey-5 [&:not(:hover)]:scrollbar-track-transparent",
                children: /*#__PURE__*/ _jsx(TranscriptSegmentMeasure, {
                    onResize: onWordResize,
                    ref: measureHandle
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": "TRANSCRIPT_SEGMENTS_SCROLL",
                ref: scrollElementRef,
                className: "w-full flex-1 overflow-y-auto pl-4 scrollbar scrollbar-track-grey-2 scrollbar-thumb-grey-7 [&:not(:hover)]:scrollbar-track-transparent",
                children: [
                    /*#__PURE__*/ _jsx(TranscriptVideoInfo, {}),
                    /*#__PURE__*/ _jsxs("div", {
                        ref: listRef,
                        className: "relative w-full",
                        style: {
                            height: virtualizer.getTotalSize()
                        },
                        children: [
                            !!((_segmentsHeightCache_current = segmentsHeightCache.current) === null || _segmentsHeightCache_current === void 0 ? void 0 : _segmentsHeightCache_current.size) && currentLineObserver,
                            /*#__PURE__*/ _jsx("div", {
                                className: "absolute left-0 top-0 flex w-full flex-col",
                                style: {
                                    transform: "translateY(".concat(((_virtualItems__start = (_virtualItems_ = virtualItems[0]) === null || _virtualItems_ === void 0 ? void 0 : _virtualItems_.start) !== null && _virtualItems__start !== void 0 ? _virtualItems__start : 0) - virtualizer.options.scrollMargin, "px)")
                                },
                                children: virtualItems.map((virtualRow)=>/*#__PURE__*/ _jsx("div", {
                                        "data-index": virtualRow.index,
                                        ref: virtualizer.measureElement,
                                        children: children({
                                            index: virtualRow.index
                                        })
                                    }, virtualRow.key))
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
_TranscriptSegmentsVirtualizedList.displayName = "TranscriptSegmentsVirtualizedList";
export const TranscriptSegmentsVirtualizedList = /*#__PURE__*/ memo(_TranscriptSegmentsVirtualizedList);
TranscriptSegmentsVirtualizedList.displayName = "TranscriptSegmentsVirtualizedList";
